import { Box, Button, Divider, Flex, IconButton, Input, Spinner } from "@chakra-ui/react";
import { Icons, Typography, useToast } from "@reconvert/react-ui-component";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { selectAppState, toggleHelpModal } from "../../../../app/slice/appSlice";
import { APP_PLATFORM, RECONVERT_IO_HELP_URL } from "../../../../config";
import { useCentralConfirmModal } from "../../../../context/ConfirmModalContext";
import useConditions from "../../../../hooks/useConditions";
import useVariants from "../../../../hooks/useVariants";
import VariantAnalytics from "../VariantAnalytics";
import useVariantAnalytics from "../../../../hooks/useVariantAnalytics";
import { Platform } from "../../../../models";
import { updateVariant } from "../../../../app/slice/conditionSlice";
import useUnsavedBar from "../../../../hooks/useUnsavedBar";
import VariantABTest from "./VariantABTest";
import { PageType } from "@reconvert/reconvert-utils";

const VariantOverview = () => {
  const { t } = useTranslation();
  const { error } = useToast();
  const { selectedCondition } = useConditions();
  const {
    selectedVariant,
    handleVariantNameUpdate,
    handleDeleteVariant,
    createABVariant,
    creatingVariant,
    selectedSlot,
    navigateToEditor,
    deletingVariant,
  } = useVariants();

  const { openConfirmModal, setConfirmLoading } = useCentralConfirmModal();
  const { pushUnsavedChanges, preventAction } = useUnsavedBar();
  const dispatch = useAppDispatch();
  const { activePage } = useAppSelector(selectAppState);
  const { variantAnalyticData, fetchAnalytics, loading } = useVariantAnalytics();
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(selectedVariant?.name);
  const [preserveName, setPreserveName] = useState(selectedVariant?.name);

  const {
    dateRange,
    view: { isAnalyticView },
  } = useAppSelector(selectAppState);

  useEffect(() => {
    setName(t(selectedVariant?.name));
    setPreserveName(selectedVariant?.name);
  }, [selectedVariant]);

  useEffect(() => {
    if (!selectedSlot?.isABTestingEnabled && isAnalyticView) {
      fetchAnalytics(selectedVariant?.id ?? "", "WIDGET");
    }
  }, [dateRange, isAnalyticView, selectedVariant]);

  const toggleEdit = () => {
    if (!name) {
      setName(preserveName);
      setIsEdit(!isEdit);
      return error(t("Condition name can't be empty"));
    }

    if (isEdit && selectedCondition) {
      pushUnsavedChanges({
        id: "variantNameChange",
        type: "VARIANT",
        saveAction: () => handleVariantNameUpdate(selectedVariant.id, selectedCondition.name, name),
        discardAction: () => {
          setName(selectedVariant.name);
          dispatch(updateVariant({ id: selectedVariant.id, name: selectedVariant.name }));
        },
      });
    }

    setIsEdit(!isEdit);
  };

  const handleNameChange = (e: any) => {
    if (e.target.value) {
      setPreserveName(e.target.value);
    }

    setName(e.target.value);
  };

  const handleOpenDeleteModal = useCallback(() => {
    openConfirmModal({
      title: t("Are you sure you want to delete {{name}}?", { name: t(selectedVariant?.name ?? "") }),
      okButtonText: t("Delete widgets"),
      cancelButtonText: t("Cancel"),
      onConfirm: handleDeleteVariant,
      onCancel: () => {},
      destructive: true,
    });
  }, [openConfirmModal, t, selectedVariant?.name, handleDeleteVariant]);

  useEffect(() => {
    if (deletingVariant) {
      setConfirmLoading(true);
    }
  }, [deletingVariant, setConfirmLoading]);

  const height = `calc(100vh - ${APP_PLATFORM === Platform.Wix ? "60px" : "0px"})`;

  const isNameTranslated = activePage === PageType.POST_PURCHASE_PAGE1
    || activePage === PageType.POST_PURCHASE_PAGE2
    || activePage === PageType.PRE_PURCHASE_CART_DRAWER_PAGE
    ? true : false;

  return (
    <Box flexBasis={"350px"} minWidth="350px" bg="white" boxShadow="md" height={height} className="variant-overview">
      <>
        <Box p={4} height={height}>
          {selectedSlot?.isABTestingEnabled ? (
            <Typography
              onDoubleClick={toggleEdit}
              textAlign={"left"}
              variant="subheading1"
              fontSize={"16px"}
              color={"neutrals.800"}
              whiteSpace={"nowrap"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {t(selectedSlot?.name)}
            </Typography>
          ) : (
            <Box>
              {isEdit ? (
                <Input
                  htmlSize={name?.length}
                  width="100%"
                  onChange={handleNameChange}
                  value={isNameTranslated ? t(name) : name}
                  size={"xs"}
                  onBlur={toggleEdit}
                />
              ) : (
                <Typography
                  onDoubleClick={toggleEdit}
                  textAlign={"left"}
                  variant="subheading1"
                  fontSize={"16px"}
                  color={"neutrals.800"}
                  whiteSpace={"nowrap"}
                  textOverflow={"ellipsis"}
                  overflow={"hidden"}
                >
                  {isNameTranslated ? t(name) : name}
                </Typography>
              )}
            </Box>
          )}
          <Box my={2}>
            {selectedSlot?.isABTestingEnabled ? (
              <Typography
                color="link.color"
                variant="body"
                as="a"
                cursor="pointer"
                className={APP_PLATFORM === Platform.Wix ? "link" : " "}
                onClick={() => {
                  const link =
                    APP_PLATFORM === Platform.Shopify
                      ? `${RECONVERT_IO_HELP_URL}/en/article/how-to-use-ab-testing-for-widgets-shopify-18ga562/reader/`
                      : `${RECONVERT_IO_HELP_URL}/en/article/how-to-use-ab-testing-for-widgets-wix-1tye8ed/reader/`;

                  dispatch(
                    toggleHelpModal({
                      link: link,
                      title: t("Learn more about A/B testing").toString(),
                    }),
                  );
                }}
              >
                {t("Learn more about A/B testing")}
              </Typography>
            ) : (
              <Typography
                color="link.color"
                variant="body"
                as="a"
                cursor="pointer"
                className={APP_PLATFORM === Platform.Wix ? "link" : " "}
                onClick={() => {
                  const link =
                    APP_PLATFORM === Platform.Shopify
                      ? `${RECONVERT_IO_HELP_URL}/en/article/what-are-slots-and-widgets-1ban4rt/reader/`
                      : `${RECONVERT_IO_HELP_URL}/en/article/what-are-slots-and-widgets-wix-p5zh8h/`;

                  dispatch(
                    toggleHelpModal({
                      link: link,
                      title: t("What are slots and widgets?").toString(),
                    }),
                  );
                }}
              >
                {t("Learn more about widgets")}
              </Typography>
            )}
          </Box>
          {!selectedSlot?.isABTestingEnabled && (
            <>
              <Divider />
              <Flex gap={2} mt={4} justifyContent="space-between" flexDirection="column">
                  <Button
                    variant="secondary"
                    size={"sm"}
                    fontSize={16}
                    px={4}
                    fontWeight={500}
                    disabled={creatingVariant}
                    isLoading={creatingVariant}
                    flexGrow={1}
                    onClick={() => preventAction(() => createABVariant())}
                  >
                    {t("Create A/B test")}
                  </Button>
                <Button
                  variant="secondary"
                  size={"sm"}
                  fontSize={16}
                  px={4}
                  fontWeight={500}
                  flexGrow={1}
                  onClick={() => preventAction(() => navigateToEditor(selectedVariant?.id))}
                >
                  <Typography variant="subheading2" fontWeight="medium" lineHeight={"20px"}>
                    {t("Edit widgets")}
                  </Typography>
                </Button>
              </Flex>
            </>
          )}

          <Box
            overflowY="auto"
            overflowX="hidden"
            height={selectedSlot?.isABTestingEnabled ? "calc(100% - 110px)" : "calc(100% - 180px)"}
            pb={"20px"}
          >
            {selectedSlot?.isABTestingEnabled ? (
              <VariantABTest />
            ) : loading ? (
              <Flex justifyContent="center" mt="4">
                <Spinner />
              </Flex>
            ) : (
              <VariantAnalytics variantAnalyticData={variantAnalyticData} />
            )}
          </Box>
        </Box>
        <Box sx={{ position: "fixed", bottom: "0px", width: "100%" }} bg="white" boxShadow="md">
          <Divider />
          {selectedSlot?.isABTestingEnabled ? (
            <Flex
              pt={4}
              pl={4}
              pr={4}
              pb={"20px"}
              onClick={() => preventAction(() => createABVariant())}
              cursor={"pointer"}
            >
              <IconButton
                height={"16px"}
                aria-label={"add-variant"}
                variant="unstyled"
                disabled={creatingVariant}
                isLoading={creatingVariant}
              >
                <>
                  <Icons.PlusCircle height={"16px"} width={"16px"} color="link.color" />
                  <Typography
                    ml={2}
                    display={"inline"}
                    alignItems={"center"}
                    variant="small"
                    color="link.color"
                    fontWeight={"medium"}
                  >
                    {t("Add variant")}
                  </Typography>
                </>
              </IconButton>
            </Flex>
          ) : (
            <Flex
              pt={4}
              pl={4}
              pr={4}
              pb={"20px"}
              onClick={() => preventAction(handleOpenDeleteModal)}
              cursor={"pointer"}
            >
              <IconButton height={"16px"} aria-label={"delete-variant"} variant="unstyled">
                <>
                  <Icons.TrashSimple height={"16px"} width={"16px"} color="error.700" />
                  <Typography
                    ml={2}
                    display={"inline"}
                    alignItems={"center"}
                    variant="small"
                    color="error.700"
                    fontWeight={"medium"}
                  >
                    {t("Delete widgets")}
                  </Typography>
                </>
              </IconButton>
            </Flex>
          )}
        </Box>
      </>
    </Box>
  );
};

export default React.memo(VariantOverview);
